import React, { FunctionComponent, ReactElement, useRef, useState } from 'react';
import styled from 'styled-components';
import { FormattedMessage, useIntl } from 'react-intl';
import getIcon from '@empiriecom/module-components/utils/getIcon';
import { Notification } from '@empiriecom/module-components/Notification';
import { formatCurrency } from '@empiriecom/module-components/FormattedCurrency';
import useConfig from '@empiriecom/module-components/hooks/useConfig';
import { FragmentConfiguration } from '@/config/default';
import { CalculatorType } from '../../types';
import { useInstallmentCalculatorContext } from '../../InstallmentCalculatorContext';

const PriceInputWrapper = styled.div`
  width: 100%;
`;

const InstallmentInputLabel = styled.label<{ calculatorType: CalculatorType }>`
  cursor: pointer;
  ${({ calculatorType }) =>
    calculatorType === 'DYNAMIC' &&
    'display: block; width: 100%; text-align: center; font-size: 0.938rem;'}
`;

const InstallmentInputIconLabel = styled.label`
  position: absolute;
  cursor: pointer;
  top: 50%;
  right: 0;
  transform: translateY(-50%);
`;

const InstallmentInputWrapper = styled.div<{ calculatorType: CalculatorType }>`
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0 auto;
  width: 100%;
  max-width: 11rem;
  position: relative;
`;

const InstallmentInput = styled.input<{ calculatorType: CalculatorType; hasSmallPrize: boolean }>`
  padding: 0.333em 1rem;
  text-align: center;
  background: 0;
  outline: none;
  width: 100%;
  ${({ calculatorType, hasSmallPrize }) =>
    calculatorType === 'DYNAMIC' && !hasSmallPrize ? 'font-size: 1.5rem;' : 'font-size: 0.9375rem;'}
  &&#installment-calculator-price {
    border: 0;
  }
  &&:focus {
    background: #ffffff;
  }
`;

const validateInputVal = (value: string): string => {
  const regExp = /^([\d]{1,2}([.]))?(\d{2,3})([,.]{1})(\d{2})(\s{1})(\D{1,3})$/g;
  return value.match(regExp) !== null ? value.match(regExp).join('') : null;
};

export const InstallmentPriceInput: FunctionComponent = (): ReactElement => {
  const {
    response,
    isPriceEditable,
    installmentPrice,
    setInstallmentPrice,
  } = useInstallmentCalculatorContext();
  const Pencil = getIcon('Edit');
  const [showIcon, setShowIcon] = useState<boolean>(isPriceEditable);
  const [inputVal, setInputVal] = useState<number>(installmentPrice);
  const [isInputInvalid, setIsInputInvalid] = useState<boolean>(false);
  const {
    installmentCalculator: { hasSmallPrize },
  } = useConfig<FragmentConfiguration>();
  const inputRef = useRef(null);
  const intl = useIntl();
  const { currency, type } = response;
  const validationHint = (
    <span>
      <span>
        <FormattedMessage
          id="installment-calculator.validation.label"
          defaultMessage="Bitte gib den Preis in folgendem Format an:"
        />
      </span>
      <span>&#160;</span>
      <strong>
        <FormattedMessage
          id="installment-calculator.validation.pattern"
          defaultMessage="X.XXX,XX €"
        />
      </strong>
    </span>
  );

  return (
    <PriceInputWrapper data-testid="installment-price-input">
      {isInputInvalid && (
        <Notification
          content={validationHint}
          isInline
          level="error"
          onClose={() => setTimeout(() => setIsInputInvalid(false), 400)}
        />
      )}
      <InstallmentInputLabel calculatorType={type} htmlFor="installment-calculator-price">
        <FormattedMessage id="installment-calculator.label.price" defaultMessage="Preis" />
      </InstallmentInputLabel>
      <InstallmentInputWrapper calculatorType={type}>
        <InstallmentInput
          calculatorType={type}
          data-testid="price-input"
          defaultValue={formatCurrency(currency, inputVal, intl)}
          disabled={!isPriceEditable}
          hasSmallPrize={hasSmallPrize}
          id="installment-calculator-price"
          name="price"
          onBlur={() => {
            setShowIcon(true);
            const validatedInputVal: string = validateInputVal(inputRef.current.value);
            const input = inputRef;
            if (validatedInputVal === null) {
              setIsInputInvalid(true);
              input.current.value = inputVal;
            } else {
              setIsInputInvalid(false);
              const value = Number(
                validatedInputVal
                  .replace('.', '')
                  .replace(',', '')
                  .replace(' ', '')
                  .replace('€', ''),
              );
              setInstallmentPrice(value);
              input.current.value = validatedInputVal;
            }
          }}
          onFocus={() => {
            setShowIcon(false);
            setInputVal(inputRef.current.value);
          }}
          onKeyPress={(e) => e.which === 13 && e.currentTarget.blur()}
          ref={inputRef}
          type="text"
        />
        {isPriceEditable && showIcon && (
          <InstallmentInputIconLabel data-testid="edit-icon" htmlFor="installment-calculator-price">
            <Pencil size="1.25rem" />
          </InstallmentInputIconLabel>
        )}
      </InstallmentInputWrapper>
    </PriceInputWrapper>
  );
};

export default InstallmentPriceInput;
